/**
 * Epic Analytics Error Boundary Component
 * 
 * Enhanced error boundary specifically for the Epic Analytics Dashboard
 * to handle component errors gracefully with improved UX and retry mechanisms.
 */

import React, { Component, ErrorInfo, ReactNode } from 'react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
  /** Component name for better error reporting */
  componentName?: string
  /** Whether to show technical details by default */
  showTechnicalDetails?: boolean
  /** Custom retry handler */
  onRetry?: () => void
}

interface State {
  hasError: boolean
  error?: Error
  errorInfo?: ErrorInfo
  retryCount: number
  isRetrying: boolean
}

/**
 * Enhanced error boundary for Epic Analytics components
 */
export class EpicAnalyticsErrorBoundary extends Component<Props, State> {
  private retryTimeoutId: NodeJS.Timeout | null = null

  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      retryCount: 0,
      isRetrying: false
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Enhanced error logging with component context
    const componentName = this.props.componentName || 'Epic Analytics Component'
    console.group(`🚨 Error in ${componentName}`)
    console.error('Error:', error)
    console.error('Error Info:', errorInfo)
    console.error('Component Stack:', errorInfo.componentStack)
    console.groupEnd()

    // Store error info for better debugging
    this.setState({ errorInfo })

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }

    // Report to error tracking service (if available)
    this.reportError(error, errorInfo)
  }

  componentWillUnmount() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId)
    }
  }

  /**
   * Report error to external service (placeholder for future implementation)
   */
  private reportError = (error: Error, errorInfo: ErrorInfo) => {
    // This could be enhanced to send errors to a service like Sentry
    const errorReport = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      componentName: this.props.componentName,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    }

    // For now, just log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.warn('Error Report:', errorReport)
    }
  }

  /**
   * Enhanced retry mechanism with exponential backoff
   */
  handleRetry = async () => {
    if (this.state.isRetrying || this.state.retryCount >= 3) return

    this.setState({ isRetrying: true })

    // Call custom retry handler if provided
    if (this.props.onRetry) {
      try {
        await this.props.onRetry()
      } catch (retryError) {
        console.error('Custom retry handler failed:', retryError)
      }
    }

    // Exponential backoff: 500ms, 1s, 2s
    const delay = Math.pow(2, this.state.retryCount) * 500

    this.retryTimeoutId = setTimeout(() => {
      this.setState(prevState => ({
        hasError: false,
        error: undefined,
        errorInfo: undefined,
        retryCount: prevState.retryCount + 1,
        isRetrying: false
      }))
    }, delay)
  }

  /**
   * Reset error state completely
   */
  handleReset = () => {
    this.setState({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      retryCount: 0,
      isRetrying: false
    })
  }

  /**
   * Get user-friendly error message based on error type
   */
  private getUserFriendlyMessage = (error: Error): string => {
    const message = error.message.toLowerCase()

    if (message.includes('network') || message.includes('fetch')) {
      return 'Unable to load analytics data. Please check your internet connection and try again.'
    }

    if (message.includes('timeout')) {
      return 'The request took too long to complete. Please try again.'
    }

    if (message.includes('unauthorized') || message.includes('403')) {
      return 'You do not have permission to view this analytics data.'
    }

    if (message.includes('not found') || message.includes('404')) {
      return 'The requested analytics data could not be found.'
    }

    if (message.includes('chart') || message.includes('canvas')) {
      return 'There was an issue rendering the chart. This might be due to browser compatibility or data format issues.'
    }

    return 'Something went wrong while loading the analytics dashboard. Please try again.'
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback
      }

      const componentName = this.props.componentName || 'Epic Analytics'
      const userMessage = this.getUserFriendlyMessage(this.state.error!)
      const canRetry = this.state.retryCount < 3

      // Enhanced default fallback UI
      return (
        <div className="epic-analytics-error-boundary" role="alert" aria-live="assertive">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-red-800">
                  {componentName} Error
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{userMessage}</p>

                  {this.state.retryCount > 0 && (
                    <p className="mt-1 text-xs text-red-600">
                      Retry attempt {this.state.retryCount} of 3
                    </p>
                  )}

                  {this.state.error && (this.props.showTechnicalDetails || process.env.NODE_ENV === 'development') && (
                    <details className="mt-3">
                      <summary className="cursor-pointer font-medium hover:text-red-900 focus:outline-none focus:underline">
                        Technical Details
                      </summary>
                      <div className="mt-2 text-xs bg-red-100 p-3 rounded border">
                        <div className="mb-2">
                          <strong>Error:</strong> {this.state.error.message}
                        </div>
                        {this.state.error.stack && (
                          <div className="mb-2">
                            <strong>Stack:</strong>
                            <pre className="mt-1 overflow-auto max-h-32 text-xs">
                              {this.state.error.stack}
                            </pre>
                          </div>
                        )}
                        {this.state.errorInfo?.componentStack && (
                          <div>
                            <strong>Component Stack:</strong>
                            <pre className="mt-1 overflow-auto max-h-32 text-xs">
                              {this.state.errorInfo.componentStack}
                            </pre>
                          </div>
                        )}
                      </div>
                    </details>
                  )}
                </div>

                <div className="mt-4 flex flex-wrap gap-3">
                  {canRetry && (
                    <button
                      onClick={this.handleRetry}
                      disabled={this.state.isRetrying}
                      className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      aria-describedby="retry-description"
                    >
                      {this.state.isRetrying ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-red-800 inline" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Retrying...
                        </>
                      ) : (
                        'Try Again'
                      )}
                    </button>
                  )}

                  <button
                    onClick={this.handleReset}
                    className="bg-white px-3 py-2 rounded-md text-sm font-medium text-red-800 border border-red-300 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
                  >
                    Reset
                  </button>

                  <button
                    onClick={() => window.location.reload()}
                    className="bg-white px-3 py-2 rounded-md text-sm font-medium text-red-800 border border-red-300 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
                  >
                    Reload Page
                  </button>

                  {!canRetry && (
                    <button
                      onClick={() => window.history.back()}
                      className="bg-gray-100 px-3 py-2 rounded-md text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                    >
                      Go Back
                    </button>
                  )}
                </div>

                {/* Hidden description for screen readers */}
                <div id="retry-description" className="sr-only">
                  Retry loading the component. This will attempt to reload the failed component.
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default EpicAnalyticsErrorBoundary